import { classNames } from '@/utils/classNames';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
    Box,
    Collapse,
    HStack,
    Icon,
    IconButton,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { Children, useState } from 'react';
import { LuArrowDown, LuArrowUp, LuX } from 'react-icons/lu';
import { MdDragIndicator } from 'react-icons/md';
import { useSelector } from 'react-redux';

import SectionHandler from './SectionHandler';

const AccordionItemPillHandler = ({
    children,
    icon,
    label,
    errors,
    touched,
    values,
    setValues,
    accordionAdornment,
    isDisabled,
    noOrder = true,
    dragHandleProps,
    orderNumber,
    platformsLength,
    isInGroup = false,
    onRemovePlatform,
    onMoveUp,
    onMoveDown,
    isFirst = false,
    isLast = false,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const childrenArray = Array.isArray(children)
        ? children
        : typeof children === 'function'
          ? [children({ errors, touched, values, setValues })]
          : [children];
    const childrenCount = Children.count(childrenArray);
    const { darkMode } = useSelector((state) => state.settings);

    const toggleExpanded = () => {
        if (!isDisabled) {
            setIsExpanded(!isExpanded);
        }
    };

    return (
        <Box mb={2}>
            {/* Pill Container */}
            <Box
                className={`${isExpanded ? 'border-b-none rounded-b-none rounded-t-md' : 'rounded-md'}`}
                h="41px"
                pl={2.5}
                pr={isInGroup ? 0 : 2.5}
                display="flex"
                alignItems="center"
                gap={3}
                bg={darkMode ? 'dark.bgDark' : 'white'}
                border="1px solid"
                borderColor={darkMode ? 'dark.tcap-borderGray' : 'gray.200'}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                opacity={isDisabled ? 0.6 : 1}
                transition="all 0.2s"
                onClick={toggleExpanded}>
                {/* Drag Handle */}
                {!noOrder && dragHandleProps && (
                    <Box
                        {...dragHandleProps}
                        cursor="grab"
                        _active={{ cursor: 'grabbing' }}
                        display="flex"
                        alignItems="center"
                        onClick={(e) => e.stopPropagation()}>
                        <Icon
                            as={MdDragIndicator}
                            fontSize="20px"
                            color={darkMode ? 'white.400' : 'gray.500'}
                            _hover={{
                                color: darkMode ? 'white.300' : 'gray.700',
                            }}
                        />
                    </Box>
                )}

                {/* Number indicator for top-level items only */}
                {!isInGroup &&
                    !noOrder &&
                    platformsLength > 1 &&
                    orderNumber && (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minW={8}
                            h={8}
                            fontWeight="semibold"
                            fontSize="sm"
                            color="messenger.500">
                            {orderNumber}
                        </Box>
                    )}

                {/* Platform Icon & Name */}
                <HStack spacing={3} flex="1" minW={0}>
                    {icon && <Icon as={icon} fontSize="25px" />}
                    <Text
                        fontWeight="semibold"
                        fontSize="md"
                        className={classNames(
                            darkMode ? 'text-white' : 'text-black',
                        )}
                        noOfLines={1}
                        flex="1">
                        {label}
                    </Text>
                </HStack>

                {/* Adornment (Status/User) - Inside the pill */}
                {accordionAdornment && (
                    <Box
                        aria-label="status/user"
                        // onClick={(e) => e.stopPropagation()}
                        sx={{
                            '& .chakra-stack': {
                                marginRight: isInGroup ? '30px' : null,
                            },
                        }}>
                        {accordionAdornment}
                    </Box>
                )}

                {/* Arrow buttons for reordering */}
                {!isInGroup && !noOrder && platformsLength > 1 && (
                    <HStack spacing={0} onClick={(e) => e.stopPropagation()}>
                        {!isFirst && onMoveUp ? (
                            <Tooltip label="Increase Priority" hasArrow>
                                <IconButton
                                    aria-label="Move up (increase priority)"
                                    icon={<LuArrowUp />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="gray"
                                    _hover={{
                                        bg: 'transparent',
                                        color: darkMode ? 'white' : 'gray.700',
                                    }}
                                    onClick={onMoveUp}
                                />
                            </Tooltip>
                        ) : (
                            <Box
                                w="32px"
                                h="32px"
                                aria-label="placeholder arrow down"
                                pointerEvents="unset"
                            />
                        )}
                        {!isLast && onMoveDown ? (
                            <Tooltip label="Decrease Priority" hasArrow>
                                <IconButton
                                    aria-label="Move down (decrease priority)"
                                    icon={<LuArrowDown />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="gray"
                                    _hover={{
                                        bg: 'transparent',
                                        color: darkMode ? 'white' : 'gray.700',
                                    }}
                                    onClick={onMoveDown}
                                />
                            </Tooltip>
                        ) : (
                            <Box
                                w="32px"
                                h="32px"
                                aria-label="placeholder arrow up"
                                pointerEvents="unset"
                            />
                        )}
                    </HStack>
                )}

                {/* Close Button - Only show when in group */}
                {isInGroup && onRemovePlatform && (
                    <IconButton
                        aria-label="Remove platform from group"
                        icon={<LuX />}
                        size="sm"
                        variant="ghost"
                        colorScheme="gray"
                        _hover={{
                            bg: 'none',
                            color: 'red',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemovePlatform();
                        }}
                    />
                )}

                <IconButton
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} icon`}
                    icon={
                        <ChevronDownIcon
                            fontSize="20px"
                            transform={
                                isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                            }
                            transition="transform 0.2s"
                        />
                    }
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    _hover={{
                        bg: 'transparent',
                        color: darkMode ? 'white' : 'gray.700',
                    }}
                />
            </Box>

            {/* Expanded Content - Below the pill */}
            <Collapse in={isExpanded} animateOpacity>
                <Box
                    // mt={2}
                    px={4}
                    py={4}
                    bg={darkMode ? 'whiteAlpha.50' : 'white'}
                    className="rounded-b-md rounded-t-none border-x border-b"
                    borderColor={
                        darkMode ? 'dark.tcap-borderGray' : 'gray.200'
                    }>
                    {Children.map(childrenArray, (child, index) => (
                        <SectionHandler
                            key={`section-handler-${index}`}
                            index={index}
                            child={child}
                            count={childrenCount}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export default AccordionItemPillHandler;
